import * as React from 'react'
import I18n from 'core/i18n'
import styled from 'styled-components'
import * as constants from '../../static/constants'
import { IWindow } from 'static/interfaces'
import { reviewService } from 'core/services'
import { Spinner, Score } from 'components/atoms'
import { ReviewCard } from 'components/molecules/ReviewCard'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: IWindow

interface IProps {
  searchParams: {
    post_ids: number[]
  }
  score?: number
  count?: number
}

export const ReviewListModalBody: React.VFC<IProps> = props => {
  const [loading, setLoading] = React.useState(true)
  const [page, setPage] = React.useState(1)
  const [currentReviews, setCurrentReviews] = React.useState([])
  const [hasMore, setHasMore] = React.useState(false)
  const [observer, setObserver] = React.useState(null)
  const sleep = (sec: number) => new Promise(resolve => setTimeout(resolve, sec * 1000))
  const observerTarget = React.useRef(null)

  const searchReviews = React.useCallback(
    async _page => {
      try {
        setLoading(true)
        await sleep(0.5)
        const { reviews, pagination } = await reviewService.search({
          ...props.searchParams,
          page: _page,
        })
        if (_page === 1) {
          setCurrentReviews(reviews)
        } else {
          setCurrentReviews(old => [...old, ...reviews])
        }
        setHasMore(!!pagination.next_page)
      } catch (e) {
        console.error(e)
      } finally {
        setLoading(false)
      }
    },
    [reviewService.search]
  )

  // 初回表示時にobserverをセット
  React.useEffect(() => {
    setObserver(
      new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting) {
            if (hasMore) {
              setPage(p => p + 1)
            }
          }
        },
        { threshold: 0.8 }
      )
    )
  }, [hasMore])

  React.useEffect(() => {
    searchReviews(page)
  }, [page])

  React.useEffect(() => {
    if (observer) {
      if (observerTarget.current) {
        observer.observe(observerTarget.current)
      }

      return () => {
        if (observerTarget.current) {
          observer.unobserve(observerTarget.current)
        }
      }
    }
  }, [observer, observerTarget.current])

  return (
    <ReviewListWrapper>
      <div className="header">
        <button
          className="closeButton"
          onClick={() => window.globalModal.closeModal()}
          type="button"
          aria-label="Close This Window"
        >
          <SvgIcon name="close" className="icon" ariaHidden={true} role="presentation" />
        </button>
        <div className="title">{I18n.t('post.traveler_reviews')}</div>
        {props.score && (
          <div className="score">
            <Score score={props.score} />
            <span className="scoreNumber">{props.score}</span>
          </div>
        )}
        {props.count && (
          <div className="count">
            <span>{props.count}</span>
            {I18n.t('post.reviews')}
          </div>
        )}
      </div>
      <div className="body">
        {currentReviews.length > 0 &&
          currentReviews.map(review => (
            <ReviewCard key={review.id} review={review} showImage={true} />
          ))}
        <div ref={observerTarget}>
          {loading && (
            <Loader>
              <Spinner />
            </Loader>
          )}
        </div>
      </div>
    </ReviewListWrapper>
  )
}

const ReviewListWrapper = styled.div`
  position: relative;
  .header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.2rem 0 1.6rem;
    .title {
      font-size: 2rem;
      font-weight: 700;
      line-height: 2.4rem;
      margin-bottom: 0.8rem;
    }
    .score {
      display: flex;
      align-items: center;
      .Score_StarsWrapper {
        width: 16rem;
        height: 3.2rem;
      }
      .Stars_Star {
        width: 3.2rem;
        height: 3.2rem;
      }
      span.scoreNumber {
        display: flex;
        align-items: center;
        line-height: 3.2rem;
        margin-left: 1.2rem;
        color: ${constants.COLORS.black};
        font-size: 2.4rem;
        font-weight: 700;
      }
    }
    .count {
      margin-top: 0.4rem;
      color: ${constants.COLORS.subText};
      font-size: 1.4rem;
      text-transform: capitalize;
      span {
        font-size: 1.6rem;
        font-weight: 700;
        margin-right: 0.4rem;
      }
    }
  }
  .closeButton {
    position: absolute;
    right: 2.4rem;
    top: 2.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3.2rem;
    height: 3.2rem;
    padding: 0;
    color: ${constants.COLORS.black};
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 50%;
    background-color: ${constants.COLORS.white};
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s, color 0.2s;
    z-index: 999;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      right: 1.6rem;
      top: 1.6rem;
    }
    svg {
      width: 2rem;
    }
    &:hover {
      color: ${constants.COLORS.white};
      background-color: ${constants.COLORS.primary};
      border-color: ${constants.COLORS.primary};
    }
  }
  .body {
    max-height: calc(80dvh - 14rem);
    min-height: 6.4rem;
    padding: 0 2.4rem;
    overflow-x: hidden;
    overflow-y: auto;
    @media screen and (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      max-height: calc(100dvh - 14rem);
    }
    > div {
      padding: 2.4rem 0;
      border-width: 0 0 0.1rem;
      border-radius: 0;
    }
  }
`
const Loader = styled.div`
  padding: 16px;
`

export default ReviewListModalBody
